import SubjectSymbol from '@/components/SubjectSymbol';
import {subjectLabel} from '@/lib/subjects';
import React, { useEffect, useRef } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { Link } from "react-router-dom";
import { MapPin, Sprout } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { formatLocation } from "@/components/FarmCard";

const markerColor = (crop = "") => {
  const c = typeof crop === "string" ? crop.toLowerCase() : "";
  if (c.includes("grape")) return "terracotta";
  if (c.includes("aromatic") || c.includes("citrus")) return "ochre";
  return "";
};

const makeIcon = (variant) =>
  L.divIcon({
    className: "",
    html: `<div class="pa-marker ${variant}"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a8 8 0 0 0-8 8c0 6 8 12 8 12s8-6 8-12a8 8 0 0 0-8-8Zm0 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Z"/></svg></div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 34],
    popupAnchor: [0, -32],
  });

const FitBounds = ({ farms }) => {
  const map = useMap();
  const key = farms.map((f) => f.id).join(",");
  useEffect(() => {
    const pts = farms
      .filter((f) => f.location?.lat != null && f.location?.lng != null)
      .map((f) => [f.location.lat, f.location.lng]);
    if (pts.length === 1) {
      map.setView(pts[0], 9, { animate: true });
    } else if (pts.length > 1) {
      map.fitBounds(pts, { padding: [60, 60], maxZoom: 8, animate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return null;
};

const FocusFarm = ({ farms, focusId, markerRefs }) => {
  const map = useMap();
  useEffect(() => {
    if (!focusId) return;
    const f = farms.find((x) => x.id === focusId);
    if (f && f.location?.lat != null && f.location?.lng != null) {
      map.flyTo([f.location.lat, f.location.lng], 10, { animate: true });
      setTimeout(() => markerRefs.current[focusId]?.openPopup(), 700);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, farms.map((x) => x.id).join(",")]);
  return null;
};

export const ExploreMap = ({ farms = [], focusId = null }) => {
  const { t, tv, lang } = useLang();
  const markerRefs = useRef({});
  const located = farms.filter((f) => f.location?.lat != null && f.location?.lng != null);

  return (
    <MapContainer
      center={[41, 8]}
      zoom={5}
      scrollWheelZoom
      className="w-full h-full z-0"
      data-testid="explore-map"
    >
      <TileLayer
        className="pa-map-tiles"
        attribution='&copy; OpenStreetMap contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {focusId ? (
        <FocusFarm farms={located} focusId={focusId} markerRefs={markerRefs} />
      ) : (
        <FitBounds farms={located} />
      )}
      {located.map((f) => (
        <Marker
          key={f.id}
          position={[f.location.lat, f.location.lng]}
          icon={makeIcon(markerColor(f.main_crop))}
          ref={(el) => {
            if (el) markerRefs.current[f.id] = el;
          }}
        >
          <Popup>
            <div data-testid={`map-popup-${f.id}`} className="font-sans">
              {(f.cover_image || f.image) ? <img src={f.cover_image || f.image} alt="" className="w-full h-28 object-cover" /> : <div className="h-28 flex items-center justify-center bg-[#EFE9DA]"><SubjectSymbol type={f.subject_type} size={56}/></div>}
              <div className="p-3">
                {f.profile_image && <img src={f.profile_image} alt="" className="w-10 h-10 object-contain rounded mb-2"/>}
                <p className="flex gap-2 items-center text-sm text-[#557448]"><SubjectSymbol type={f.subject_type} size={22}/>{subjectLabel(f.subject_type,lang)}</p>
                <h4 className="font-serif text-base font-bold text-[#2A2521] leading-tight">
                  {f.name}
                </h4>
                <p className="flex items-center gap-1 text-xs text-[#6E6258] mt-1">
                  <MapPin size={12} className="text-[#C46A3A]" />
                  {formatLocation(f.location, tv)}
                </p>
                <p className="flex items-center gap-1 text-xs text-[#4A443D] mt-1">
                  <Sprout size={12} className="text-[#557448]" />
                  {tv(f.quick_facts?.crop_type) || tv(f.main_crop)}
                </p>
                <Link
                  to={f.public_path || `/farm/${f.id}`}
                  data-testid={`map-view-farm-${f.id}`}
                  className="mt-2.5 inline-block w-full text-center rounded-full bg-[#557448] text-[#F8F5EA] text-xs font-semibold py-1.5 hover:bg-[#46633b] transition-colors"
                >
                  {t("explore.viewFarm")}
                </Link>
              </div>
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};


